import DataStore from 'src/core/stores/data-store';
import FormStore from 'src/core/stores/form-store';
import { CommandResult } from 'src/core/stores/types';
import { Languages } from 'src/i18n';

export type TutorialLocalizedData = {
  language: Languages;
  title: string;
  description: string;
};

export interface NewTutorialFunction {
  functionTitle: string;
  order: number;
}

export interface TutorialFunction extends NewTutorialFunction {
  id: string;
}

export interface TutorialSummary {
  id: string;
  universalId: string;
  title: string;
  description: string;
  titleSpanish?: string;
  descriptionSpanish?: string;
  titleFrench?: string;
  descriptionFrench?: string;
  titlePortuguese?: string;
  descriptionPortuguese?: string;
  titleRussian?: string;
  descriptionRussian?: string;
  link: string;
  functionId?: string;
  functionTitle?: string;
  functionOrder?: number;
  durationInSeconds: number;
  isEnabled: boolean;
  permissions: { id: string; permissionId: string; permissionName: string }[];
}

export const getTutorialDurationLabel = (item: TutorialSummary) => {
  if (item.durationInSeconds === undefined) return '';
  const hours = Math.floor(item.durationInSeconds / 60 / 60);
  const hoursLabel = hours > 0 ? `${hours.toString().padStart(2, '0')}:` : '';
  const minutes = Math.floor((item.durationInSeconds / 60) % 60);

  const seconds = Math.floor(item.durationInSeconds % 60);
  return `${hoursLabel}${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

export const getLocalizedDataFromTutorialItem = (item: TutorialSummary, language: Languages) => {
  if (item.title === undefined && item.description === undefined) return { title: '', description: '' };
  switch (language) {
    case Languages.English:
      return { title: item.title, description: item.description };
    case Languages.French:
      return { title: item.titleFrench ?? item.title, description: item.descriptionFrench ?? item.description };
    case Languages.Portuguese:
      return { title: item.titlePortuguese ?? item.title, description: item.descriptionPortuguese ?? item.description };
    case Languages.Russian:
      return { title: item.titleRussian ?? item.title, description: item.descriptionRussian ?? item.description };
    case Languages.Spanish:
      return { title: item.titleSpanish ?? item.title, description: item.descriptionSpanish ?? item.description };
    default:
      return { title: item.title, description: item.description };
  }
};

export const getYoutubeVideoUrlFromId = (id: string) => `https://www.youtube.com/embed/${id}`;

export function extractYouTubeVideoId(input: string): string | null {
  try {
    if (/^[a-zA-Z0-9_-]{11}$/.test(input)) {
      return input;
    }

    const url = new URL(input.trim());

    if (url.hostname === 'youtu.be') {
      const id = url.pathname.split('/')[1];
      return isValidYouTubeId(id) ? id : null;
    }

    const v = url.searchParams.get('v');
    if (isValidYouTubeId(v)) {
      return v;
    }

    const parts = url.pathname.split('/').filter(Boolean);

    const knownPrefixes = ['embed', 'shorts', 'live'];
    if (knownPrefixes.includes(parts[0]) && isValidYouTubeId(parts[1])) {
      return parts[1];
    }

    return null;
  } catch {
    return null;
  }
}

export const getYoutubeThumbnailUrlFromId = (id: string) => `https://img.youtube.com/vi/${id}/maxresdefault.jpg`;

function isValidYouTubeId(value: string | null | undefined): value is string {
  return !!value && /^[a-zA-Z0-9_-]{11}$/.test(value);
}

export class TutorialSummaryStore extends DataStore<TutorialSummary> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/video-tutorial`, []);
  }

  public async synchronizeVideos() {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.post<{}, CommandResult<{}>>(`${this.baseUrl}/sync`, {});
      return response.data;
    });
  }

  public async deleteItems(items: string[]) {
    return await this.handleCallAsync(async () => {
      return await this.httpService.delete<{ videoTutorialIds: string[] }, CommandResult<{ videoTutorialIds: string[] }>>(`${this.baseUrl}`, {
        videoTutorialIds: items,
      });
    });
  }
}

export class TutorialItemStore extends FormStore<TutorialSummary, TutorialSummary> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/video-tutorial`);
  }

  public async load(id: string) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<TutorialSummary>(`${this.baseUrl}/${encodeURIComponent(id)}`);

      this._state.set((s) => {
        s.item = response.data;
        return s;
      });
      return true;
    });
  }

  public async create(item: Partial<TutorialSummary>) {
    return await this.handleCallAsync(async () => {
      try {
        var result = await this.httpService.post<Partial<TutorialSummary>, CommandResult<TutorialSummary>>(`${this.baseUrl}`, item);
        return result.data;
      } catch (e) {
        return { isSuccess: false, error: e as string, messages: [] };
      }
    });
  }

  public async save(id: string, item: Partial<TutorialSummary>) {
    return await this.handleCallAsync(async () => {
      try {
        var result = await this.httpService.put<Partial<TutorialSummary>, CommandResult<TutorialSummary>>(`${this.baseUrl}/${id}`, item);
        return result.data;
      } catch (e) {
        return { isSuccess: false, error: e as string, messages: [] };
      }
    });
  }

  public async updatePermissions(id: string, perms: string[]) {
    return await this.handleCallAsync(async () => {
      try {
        var result = await this.httpService.post<{ permissions: string[] }, CommandResult<TutorialSummary>>(`${this.baseUrl}/${id}/permissions`, {
          permissions: perms,
        });
        return result.data;
      } catch (e) {
        return { isSuccess: false, error: e as string, messages: [] };
      }
    });
  }

  public async synchronizeVideo(id: string) {
    return await this.handleCallAsync(async () => {
      try {
        var response = await this.httpService.post<{}, CommandResult<{}>>(`${this.baseUrl}/sync/${id}`, {});
        return response.data;
      } catch (e) {
        return { isSuccess: false, error: e as string, messages: [] };
      }
    });
  }
}

export class TutorialFunctionSummaryStore extends DataStore<TutorialFunction> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/video-tutorial/functions`, []);
  }
}

export class TutorialFunctionItemStore extends FormStore<TutorialFunction, NewTutorialFunction> {
  newItemUrl: string;
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/video-tutorial/functions`);
    this.newItemUrl = `${baseUrl}/identity/api/video-tutorial/function`;
  }

  public async create(item: NewTutorialFunction) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<NewTutorialFunction, CommandResult<TutorialFunction>>(`${this.newItemUrl}`, item);
      return result.data;
    });
  }
}
